import React from "react";
import { X } from "lucide-react";

// ─── InAppBrowserBanner ───────────────────────────────────────────────────────
// Instagram, Facebook and TikTok open links in their own browser, where the
// iOS Smart App Banner (index.html `apple-itunes-app`) never shows and there
// is no Android equivalent. Ad traffic lands there, so this bar offers the
// store link instead. On Android it shows in every browser. Phones only;
// dismissal is remembered for 30 days.
// Pinned to the bottom: the landing top bar is fixed and the app shell is
// full-height, so a bar in the page flow at the top would clash with both.

const APP_STORE_URL = "https://apps.apple.com/gb/app/atlas-hiking-record/id6788635039";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=co.uk.goatlas.app";
const DISMISS_KEY = "atlas:in-app-banner-dismissed-at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

// Instagram, Facebook/Messenger, TikTok in-app browser user agents.
const IN_APP_RE = /Instagram|FBAN|FBAV|FB_IAB|musical_ly|BytedanceWebview|TikTok/i;

type Platform = "ios" | "android";

// iOS: in-app browsers only (Safari has the Smart App Banner).
// Android: every browser, since nothing native covers it. Plain WebViews
// ("; wv)") inside other apps are skipped unless they are one we target.
function detectPlatform(): Platform | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  const inApp = IN_APP_RE.test(ua);
  if (/iPhone|iPad|iPod/i.test(ua)) return inApp ? "ios" : null;
  if (/Android/i.test(ua)) return inApp || !/; wv\)/.test(ua) ? "android" : null;
  return null;
}

function recentlyDismissed(): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_MS;
  } catch {
    return false;
  }
}

export function InAppBrowserBanner() {
  const [platform] = React.useState(detectPlatform);
  const [hidden, setHidden] = React.useState(() => platform == null || recentlyDismissed());

  // Chrome on Android offers to install the site as a web app (the manifest
  // makes it installable). That is not the Play app, so stop the automatic
  // prompt; "Install app" stays in Chrome's menu for anyone who wants it.
  React.useEffect(() => {
    if (platform !== "android") return;
    const block = (e: Event) => e.preventDefault();
    window.addEventListener("beforeinstallprompt", block);
    return () => window.removeEventListener("beforeinstallprompt", block);
  }, [platform]);

  if (platform == null || hidden) return null;

  function dismiss() {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Storage blocked: hide for this page view only.
    }
    setHidden(true);
  }

  const storeUrl = platform === "ios" ? APP_STORE_URL : GOOGLE_PLAY_URL;
  const storeName = platform === "ios" ? "App Store" : "Google Play";

  return (
    <div
      role="region"
      aria-label="Get the Atlas app"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px calc(10px + env(safe-area-inset-bottom))",
        background: "var(--surface-raised)",
        borderTop: "1px solid var(--border-strong)",
        boxShadow: "0 -6px 20px rgba(0, 0, 0, 0.25)",
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          padding: 4,
          margin: "0 -4px",
          cursor: "pointer",
          color: "var(--text-muted)",
          display: "flex",
        }}
      >
        <X size={16} />
      </button>
      <img
        src="/icon-192.png"
        alt=""
        width={40}
        height={40}
        style={{ borderRadius: 9, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Atlas</div>
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>Free on {storeName}</div>
      </div>
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener"
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: "var(--control-sm)",
          padding: "0 14px",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-strong)",
          background: "var(--surface-sunken)",
          color: "var(--text-primary)",
          fontSize: 13,
          fontWeight: 600,
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        Get the app
      </a>
    </div>
  );
}
